<template>
    <header
        class="sticky top-0 z-50 transition-[padding] duration-300"
        :class="scrolled ? 'py-2.5' : 'py-4'"
    >
        <div class="relative flex w-full items-center justify-between px-6 lg:px-12">
            <!-- Logo: full wordmark at top, collapses to the mark on scroll -->
            <RouterLink
                :to="getRoute('/')"
                class="relative z-10 flex h-11 items-center transition-[width] duration-[400ms] ease-out"
                :class="scrolled ? 'w-11' : 'w-[150px]'"
                aria-label="BitPeak home"
                @click="closeAll"
            >
                <img
                    src="/images/brand/bitpeak-logo-primary.svg"
                    alt="BitPeak"
                    width="150"
                    height="32"
                    decoding="async"
                    fetchpriority="high"
                    class="absolute left-0 top-1/2 h-8 w-auto origin-left -translate-y-1/2 transition-all duration-300"
                    :class="scrolled ? 'pointer-events-none scale-90 opacity-0' : 'scale-100 opacity-100'"
                />
                <span
                    class="absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_6px_20px_-8px_rgba(24,22,16,0.45)] ring-1 ring-black/5 transition-all duration-300"
                    :class="scrolled ? 'scale-100 opacity-100' : 'pointer-events-none scale-75 opacity-0'"
                    aria-hidden="true"
                >
                    <img src="/favicon/apple-touch-icon.png" alt="" width="24" height="24" class="h-6 w-6" decoding="async" />
                </span>
            </RouterLink>

            <!-- Center nav (transparent at top, becomes a pill once scrolled) -->
            <nav
                class="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 rounded-full border border-black/5 bg-white p-1.5 shadow-[0_6px_24px_-10px_rgba(24,22,16,0.25)] transition-all duration-300 xl:flex"
                aria-label="Primary navigation"
                @mouseleave="closeSoon"
            >
                <template v-for="link in links" :key="link.to">
                    <RouterLink
                        v-if="link.menu"
                        :to="getRoute(link.to)"
                        class="flex items-center gap-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors"
                        :class="activeMenu === link.menu ? 'bg-background-light text-text-main' : 'text-text-main hover:bg-background-light/70'"
                        @mouseenter="openMenu(link.menu)"
                        @click="closeAll"
                    >
                        {{ t(link.label) }}
                        <span
                            class="material-symbols-outlined text-[18px] transition-transform duration-200"
                            :class="activeMenu === link.menu ? 'rotate-180' : ''"
                        >expand_more</span>
                    </RouterLink>

                    <RouterLink
                        v-else
                        :to="getRoute(link.to)"
                        class="whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-text-main transition-colors hover:bg-background-light/70"
                        @mouseenter="closeNow"
                        @click="closeAll"
                    >
                        {{ t(link.label) }}
                    </RouterLink>
                </template>

                <RouterLink
                    :to="getRoute('/contact')"
                    class="ml-1 whitespace-nowrap rounded-full px-5 py-2 text-sm font-bold transition-colors duration-300"
                    :class="scrolled ? 'bg-primary text-text-main hover:brightness-105' : 'bg-text-main text-white hover:bg-text-main/90'"
                    @mouseenter="closeNow"
                    @click="closeAll"
                >
                    {{ t('nav.start_project') }}
                </RouterLink>
            </nav>

            <!-- Right: language selector (fades out on scroll, Finom-style) -->
            <div
                class="hidden items-center transition-all duration-300 xl:flex"
                :class="scrolled ? 'pointer-events-none translate-x-2 opacity-0' : 'opacity-100'"
            >
                <button
                    class="flex items-center gap-2 rounded-full border border-black/10 bg-white/80 px-3.5 py-2 text-xs font-bold text-text-main transition-colors hover:border-text-main/40"
                    @click="toggleLocale"
                    :aria-label="'Switch language (current: ' + locale + ')'"
                    type="button"
                >
                    <span class="material-symbols-outlined text-[16px] text-primary">language</span>
                    {{ locale.toUpperCase() }}
                </button>
            </div>

            <!-- Mobile toggle -->
            <button
                class="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-text-main shadow-sm ring-1 ring-black/5 xl:hidden"
                type="button"
                @click="open = !open"
                :aria-expanded="open"
                aria-label="Open menu"
            >
                <span class="material-symbols-outlined">{{ open ? 'close' : 'menu' }}</span>
            </button>

            <!-- Mega-menu panel -->
            <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
            >
                <div
                    v-if="activeMenu"
                    class="absolute left-1/2 top-full hidden -translate-x-1/2 pt-3 xl:block"
                    @mouseenter="cancelClose"
                    @mouseleave="closeSoon"
                >
                    <div class="w-[min(92vw,900px)] rounded-3xl border border-black/5 bg-white p-3 shadow-[0_30px_80px_-30px_rgba(24,22,16,0.5)]">
                        <!-- SERVICES -->
                        <div v-if="activeMenu === 'services'" class="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
                            <RouterLink :to="getRoute('/#services')" class="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-primary/5 to-transparent p-6" @click="closeAll">
                                <div>
                                    <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-text-main"><span class="material-symbols-outlined">smart_toy</span></span>
                                    <h3 class="mt-5 text-lg font-black tracking-tight text-text-main">{{ t('menu.services_featured_title') }}</h3>
                                    <p class="mt-2 text-sm text-text-muted">{{ t('menu.services_featured_text') }}</p>
                                </div>
                                <span class="mt-6 inline-flex items-center gap-1 text-sm font-bold text-text-main">{{ t('menu.services_cta') }}<span class="material-symbols-outlined text-[18px] transition-transform group-hover:translate-x-1">arrow_forward</span></span>
                            </RouterLink>

                            <div class="grid grid-cols-2 gap-1.5">
                                <RouterLink v-for="s in serviceItems" :key="s.title" :to="getRoute('/#services')" class="group rounded-2xl p-4 transition-colors hover:bg-background-light" @click="closeAll">
                                    <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary transition-colors group-hover:bg-primary group-hover:text-text-main"><span class="material-symbols-outlined text-[20px]">{{ s.icon }}</span></span>
                                    <h4 class="mt-3 text-sm font-bold text-text-main">{{ t(s.title) }}</h4>
                                    <p class="mt-1 text-xs leading-relaxed text-text-muted">{{ t(s.text) }}</p>
                                </RouterLink>
                            </div>
                        </div>

                        <!-- OUTSOURCING -->
                        <div v-else-if="activeMenu === 'outsourcing'" class="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
                            <RouterLink :to="getRoute('/outsourcing')" class="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-primary/5 to-transparent p-6" @click="closeAll">
                                <div>
                                    <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-text-main"><span class="material-symbols-outlined">public</span></span>
                                    <h3 class="mt-5 text-lg font-black tracking-tight text-text-main">{{ t('menu.outsourcing_featured_title') }}</h3>
                                    <p class="mt-2 text-sm text-text-muted">{{ t('menu.outsourcing_featured_text') }}</p>
                                </div>
                                <span class="mt-6 inline-flex items-center gap-1 text-sm font-bold text-text-main">{{ t('menu.outsourcing_cta') }}<span class="material-symbols-outlined text-[18px] transition-transform group-hover:translate-x-1">arrow_forward</span></span>
                            </RouterLink>

                            <div class="grid grid-cols-2 gap-1.5">
                                <RouterLink v-for="o in outsourcingItems" :key="o.to" :to="getRoute(o.to)" class="group rounded-2xl p-4 transition-colors hover:bg-background-light" @click="closeAll">
                                    <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary transition-colors group-hover:bg-primary group-hover:text-text-main"><span class="material-symbols-outlined text-[20px]">{{ o.icon }}</span></span>
                                    <h4 class="mt-3 text-sm font-bold text-text-main">{{ t(o.title) }}</h4>
                                    <p class="mt-1 text-xs leading-relaxed text-text-muted">{{ t(o.text) }}</p>
                                </RouterLink>
                            </div>
                        </div>

                        <!-- PROJECTS -->
                        <div v-else-if="activeMenu === 'projects'" class="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
                            <RouterLink :to="getRoute('/projects')" class="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-background-dark p-6 text-white" @click="closeAll">
                                <div>
                                    <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-text-main"><span class="material-symbols-outlined">grid_view</span></span>
                                    <h3 class="mt-5 text-lg font-black tracking-tight">{{ t('menu.projects_featured_title') }}</h3>
                                    <p class="mt-2 text-sm text-white/60">{{ t('menu.projects_featured_text') }}</p>
                                </div>
                                <span class="mt-6 inline-flex items-center gap-1 text-sm font-bold text-primary">{{ t('menu.projects_cta') }}<span class="material-symbols-outlined text-[18px] transition-transform group-hover:translate-x-1">arrow_forward</span></span>
                            </RouterLink>

                            <div class="grid grid-cols-2 gap-1.5">
                                <RouterLink v-for="p in projectItems" :key="p.slug" :to="{ name: 'project-details', params: { slug: p.slug, locale } }" class="group flex items-center gap-3 rounded-2xl p-3 transition-colors hover:bg-background-light" @click="closeAll">
                                    <span class="h-14 w-16 shrink-0 overflow-hidden rounded-xl bg-gray-100">
                                        <img :src="p.image" :alt="p.title" class="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                                    </span>
                                    <span class="min-w-0">
                                        <span class="block text-[11px] font-bold uppercase tracking-wide text-primary">{{ p.tag }}</span>
                                        <span class="block truncate text-sm font-bold text-text-main">{{ p.title }}</span>
                                    </span>
                                </RouterLink>
                            </div>
                        </div>

                        <!-- TEAM -->
                        <div v-else-if="activeMenu === 'team'" class="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.9fr)]">
                            <RouterLink :to="getRoute('/team')" class="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-primary/5 to-transparent p-6" @click="closeAll">
                                <div>
                                    <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-text-main"><span class="material-symbols-outlined">groups</span></span>
                                    <h3 class="mt-5 text-lg font-black tracking-tight text-text-main">{{ t('menu.team_featured_title') }}</h3>
                                    <p class="mt-2 text-sm text-text-muted">{{ t('menu.team_featured_text') }}</p>
                                </div>
                                <span class="mt-6 inline-flex items-center gap-1 text-sm font-bold text-text-main">{{ t('menu.team_cta') }}<span class="material-symbols-outlined text-[18px] transition-transform group-hover:translate-x-1">arrow_forward</span></span>
                            </RouterLink>

                            <div class="grid grid-cols-3 gap-1.5">
                                <RouterLink v-for="m in teamItems" :key="m.name" :to="getRoute('/team')" class="group flex flex-col items-center rounded-2xl p-3 text-center transition-colors hover:bg-background-light" @click="closeAll">
                                    <span class="h-14 w-14 overflow-hidden rounded-full bg-gray-100 ring-2 ring-transparent transition group-hover:ring-primary/40">
                                        <img :src="m.image" :alt="m.name" class="h-full w-full object-cover" loading="lazy" />
                                    </span>
                                    <span class="mt-2 text-sm font-bold text-text-main">{{ m.name }}</span>
                                    <span class="text-xs text-text-muted">{{ m.role }}</span>
                                </RouterLink>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <!-- Mobile menu -->
        <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div v-if="open" class="relative mx-4 mt-2 rounded-2xl border border-black/5 bg-white p-4 shadow-xl xl:hidden">
                <div class="flex flex-col gap-1">
                    <template v-for="link in links" :key="link.to">
                        <RouterLink
                            :to="getRoute(link.to)"
                            class="rounded-xl px-4 py-3 text-sm font-medium text-text-main hover:bg-background-light"
                            @click="closeAll"
                        >
                            {{ t(link.label) }}
                        </RouterLink>
                        <RouterLink
                            v-if="link.menu === 'outsourcing'"
                            :to="getRoute('/outsourcing/apply')"
                            class="-mt-1 rounded-xl px-4 py-2 pl-8 text-sm font-medium text-text-muted hover:bg-background-light"
                            @click="closeAll"
                        >
                            {{ t('menu.outsourcing_work_title') }}
                        </RouterLink>
                    </template>

                    <div class="mt-2 flex items-center gap-3 border-t border-black/5 pt-3">
                        <button
                            class="flex items-center gap-2 rounded-full border border-black/10 px-3.5 py-2 text-xs font-bold text-text-main"
                            @click="toggleLocale"
                            type="button"
                        >
                            <span class="material-symbols-outlined text-[16px] text-primary">language</span>
                            {{ locale.toUpperCase() }}
                        </button>

                        <RouterLink
                            :to="getRoute('/contact')"
                            class="flex flex-1 items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-bold text-text-main"
                            @click="closeAll"
                        >
                            {{ t('nav.start_project') }}
                        </RouterLink>
                    </div>
                </div>
            </div>
        </transition>
    </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { RouterLink, useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { projects, type ProjectItem, type ProjectLocale } from '../../data/projects';
import { teamMembers } from '../../data/team';

const open = ref(false);
const scrolled = ref(false);
const activeMenu = ref<string | null>(null);
const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const links = [
    { to: '/#services', label: 'nav.services', menu: 'services' },
    { to: '/outsourcing', label: 'nav.outsourcing', menu: 'outsourcing' },
    { to: '/projects', label: 'nav.projects', menu: 'projects' },
    { to: '/team', label: 'nav.team', menu: 'team' },
    { to: '/careers', label: 'nav.careers' },
    { to: '/success-stories', label: 'nav.success_stories' },
    { to: '/contact', label: 'nav.contact' },
] as { to: string; label: string; menu?: string }[];

const activeLocale = computed<ProjectLocale>(() => (locale.value === 'nl' ? 'nl' : 'en'));

// Outsourcing serves two audiences; the menu sends each straight to their page.
const outsourcingItems = [
    { to: '/outsourcing', icon: 'business_center', title: 'menu.outsourcing_hire_title', text: 'menu.outsourcing_hire_text' },
    { to: '/outsourcing/apply', icon: 'person_search', title: 'menu.outsourcing_work_title', text: 'menu.outsourcing_work_text' },
];

const serviceItems = [
    { icon: 'smart_toy', title: 'services.s1_title', text: 'services.s1_text' },
    { icon: 'web', title: 'services.s2_title', text: 'services.s2_text' },
    { icon: 'smartphone', title: 'services.s3_title', text: 'services.s3_text' },
    { icon: 'brush', title: 'services.s4_title', text: 'services.s4_text' },
];

const projectShots: Record<string, string> = {
    'food-la-route-platform': '/images/deskop_foodlaroute.webp',
    'broednest-platform': '/images/desktop_broednest.webp',
    'highmark-software-website': '/images/desktop_highmark.webp',
    'sman-cars-platform': '/images/desktop_smancars.webp',
};
const projectSlugs = Object.keys(projectShots);

const projectItems = computed(() =>
    projectSlugs
        .map((slug) => projects.find((p) => p.slug === slug))
        .filter((p): p is ProjectItem => Boolean(p))
        .map((p) => {
            const c = p[activeLocale.value] ?? p.en;
            return { slug: p.slug, title: c.title, tag: c.tag, image: projectShots[p.slug] ?? p.image };
        })
);

const teamItems = computed(() =>
    teamMembers.slice(0, 6).map((m) => ({
        name: m.name,
        image: m.image,
        role: (activeLocale.value === 'nl' ? m.nl.role : m.en.role),
    }))
);

// Hover-intent open/close so moving across the gap to the panel keeps it open.
let closeTimer: ReturnType<typeof setTimeout> | null = null;
const cancelClose = () => { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } };
const openMenu = (key?: string) => { cancelClose(); activeMenu.value = key ?? null; };
const closeNow = () => { cancelClose(); activeMenu.value = null; };
const closeSoon = () => { cancelClose(); closeTimer = setTimeout(() => (activeMenu.value = null), 140); };
const closeAll = () => { closeNow(); open.value = false; };

let ticking = false;
const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
        scrolled.value = window.scrollY > 24;
        ticking = false;
    });
};

onMounted(() => {
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
});
onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll);
    cancelClose();
});

const toggleLocale = () => {
    const newLocale = locale.value === 'en' ? 'nl' : 'en';
    const currentPath = route.path;
    const currentLocalePrefix = `/${locale.value}`;

    let newPath: string;
    if (currentPath.startsWith(currentLocalePrefix)) {
        newPath = currentPath.replace(currentLocalePrefix, `/${newLocale}`);
    } else if (locale.value === 'en' && !currentPath.startsWith('/en')) {
        newPath = `/${newLocale}${currentPath}`;
    } else {
        newPath = `/${newLocale}`;
    }

    newPath = newPath.replace('//', '/');
    router.push(newPath);
};

const getRoute = (path: string) => {
    const currentLocale = locale.value;
    if (path.startsWith('/')) return `/${currentLocale}${path}`.replace('//', '/');
    return path;
};
</script>
